import { TONE_CLASS } from "@/components/batch-test/batchCells";
import { Badge, Card, CardHead } from "@/components/ui";
import { cn } from "@/lib/utils";
import { formatScore } from "@/utils/rubric";
import {
  EXECUTION_GROUP,
  finalFormula,
  type JudgeCriterion,
  type JudgeGroup,
  type JudgementV1,
  type JudgementV2,
  type JudgePenalty,
  measuredText,
  type Verdict,
  verdictLabel,
} from "./judgement";

/**
 * The Judge's detail for one cell (fe/TEST_CASES_RESULT.md §5): what the
 * rubric v2 score is made of — three scores, the four groups, every
 * criterion's verdict and weight, the penalties with their offsets — or,
 * for a v1 row, its three axes, read-only (be/RUBRIC.md R9.4.2 · R10).
 */

export function scoreText(value: number | null) {
  return value == null ? "—" : formatScore(value);
}

/** Weights and contributions carry three places — 0.075 is not 0.08. */
function w3(value: number | null) {
  return value == null ? "—" : value.toFixed(3);
}

function signed3(value: number | null) {
  if (value == null) return "—";
  return `${value > 0 ? "+" : value < 0 ? "−" : ""}${Math.abs(value).toFixed(3)}`;
}

/**
 * How Final came out of its parts (R3.1), so a low score says whether the
 * answer or the run cost it. The stored score is the one shown; a recomputed
 * one that differs is said, not silently replaced (R10).
 */
export function FinalFormula({ judgement }: { judgement: JudgementV2 }) {
  if (judgement.policy === "reviewed_criteria") return <span>확정 기준의 가중치 합 · {scoreText(judgement.scores.final)}</span>;
  const f = finalFormula(judgement);
  if (!f) {
    return (
      <span className="text-muted-foreground">
        Execution 이 없어 Final 을 만들지 않습니다 — Answer Quality 만 봅니다 (R3.9)
      </span>
    );
  }
  const { answerQuality, execution, final } = judgement.scores;
  return (
    <span className="tabular-nums">
      clamp({f.aqWeight.toFixed(2)} × AQ {scoreText(answerQuality)} + {f.exWeight.toFixed(2)} × EX{" "}
      {scoreText(execution)}
      {f.penalty !== 0 && ` ${f.penalty < 0 ? "−" : "+"} 감점 ${Math.abs(f.penalty).toFixed(3)}`}) ={" "}
      {scoreText(final ?? f.computed)}
      {!f.matches && (
        <span className="text-warning-foreground"> · 다시 계산하면 {formatScore(f.computed)} — 저장된 값을 씁니다</span>
      )}
    </span>
  );
}

export function GroupTable({ groups }: { groups: JudgeGroup[] }) {
  const excluded = groups.flatMap((g) => g.excluded.map((e) => ({ ...e, group: g.title })));
  return (
    <div className="border-t border-border px-4 py-3">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <caption className="sr-only">그룹별 점수</caption>
          <thead>
            <tr className="text-left text-[11px] text-muted-foreground">
              <th className="py-1 pr-2 font-normal">그룹</th>
              <th className="px-2 py-1 text-right font-normal">가중치</th>
              <th className="px-2 py-1 text-right font-normal">충족 비율</th>
              <th className="px-2 py-1 text-right font-normal" title="평가에 들어간 기준 가중치의 합 — N/A 는 빠진다">
                분모
              </th>
              <th
                className="py-1 pl-2 text-right font-normal"
                title="Final 에서 이 그룹이 차지하는 몫 — N/A 그룹의 몫은 같은 쪽 그룹끼리 나눈다"
              >
                실효 가중치
              </th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.key} className="border-t border-border">
                <td className="py-1.5 pr-2">
                  {g.title}
                  {g.key === "category" && g.domain && <span className="text-muted-foreground"> · {g.domain}</span>}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">{w3(g.weight)}</td>
                {g.ratio == null ? (
                  <td colSpan={3} className="py-1.5 pl-2 text-right text-muted-foreground">
                    {g.key === "category" && !g.domain
                      ? "N/A — 도메인 없음 (BE-DOMAIN)"
                      : "N/A — 모든 기준이 평가에서 빠졌습니다"}
                  </td>
                ) : (
                  <>
                    <td className="px-2 py-1.5 text-right tabular-nums">{w3(g.ratio)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{w3(g.denominator)}</td>
                    <td className="py-1.5 pl-2 text-right tabular-nums">{w3(g.effectiveWeight)}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {excluded.length > 0 && (
        <ul className="mt-2 grid gap-0.5 text-[12px] text-muted-foreground">
          {excluded.map((e) => (
            <li key={`${e.group}-${e.id}`}>
              {e.group} 에서 뺌: <span className="font-mono">{e.id}</span> —{" "}
              {e.reason === "execution_criteria" ? "실행 성격 기준 — Execution 이 잰다 (R6.3)" : e.reason}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Rubric v1's three axes and findings — read-only, never compared (R10). */
export function V1Axes({ judgement }: { judgement: JudgementV1 }) {
  return (
    <>
      {judgement.axes.length > 0 && (
        <ul className="grid gap-2 border-t border-border px-4 py-3">
          {judgement.axes.map((axis) => (
            <li key={axis.key} className="grid grid-cols-[40px_minmax(0,1fr)] gap-3 text-[13px]">
              {/* v1's 1–5 axes: no v2 bucket colour on them. */}
              <span className={cn("h-fit rounded-md py-0.5 text-center tabular-nums", TONE_CLASS.legacy)}>
                {axis.score}
              </span>
              <div className="grid gap-0.5">
                <span className="font-medium">{axis.title}</span>
                <span className="text-muted-foreground">{axis.reason || "—"}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
      {judgement.findings.length > 0 && (
        <div className="border-t border-border px-4 py-3">
          <p className="mb-2 text-xs font-medium">
            Specific findings{" "}
            <span className="font-normal text-muted-foreground">· v1 은 점수를 매기지 않았다 · 축 판단의 근거</span>
          </p>
          <ul className="grid gap-2">
            {judgement.findings.map((finding, i) => (
              <li key={i} className="grid grid-cols-[64px_minmax(0,1fr)] gap-3 text-[13px]">
                <Badge
                  size="compact"
                  className={cn(
                    "h-fit justify-center",
                    finding.met ? "" : "border-destructive-line bg-destructive-surface text-destructive",
                  )}
                >
                  {finding.met ? "met" : "not met"}
                </Badge>
                <div className="grid gap-0.5">
                  <span className="font-medium">{finding.criterion || "—"}</span>
                  <span className="text-muted-foreground">{finding.note}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

/**
 * A verdict, told by form as well as word. On a penalty the bad outcome is
 * `met` — it happened (R3.7) — so the warning tone follows the meaning, not
 * the value.
 */
function VerdictChip({ verdict, raw, penalty = false }: { verdict: Verdict | null; raw: string; penalty?: boolean }) {
  const bad = penalty ? verdict === "met" || verdict === "partial" : verdict === "unmet";
  return (
    <Badge
      size="compact"
      className={cn(
        "h-fit justify-center whitespace-nowrap",
        bad && "border-destructive-line bg-destructive-surface text-destructive",
        verdict === "not_applicable" && "border-dashed bg-transparent text-muted-foreground",
        verdict === "partial" && !penalty && "text-muted-foreground",
      )}
    >
      {verdictLabel(verdict, raw, penalty)}
    </Badge>
  );
}

const GROUP_ORDER = ["common", "category", "case", EXECUTION_GROUP];

/**
 * Every criterion in seven columns (R9.4.2): which, its group, the verdict,
 * its weight in the group, its share of Final, what it contributed, and why.
 * Then the penalties — nominal and applied both, so an offset is visible
 * (R6.1) — and the ones dropped as duplicates of another (R6.2).
 */
export function CriteriaCard({ judgement }: { judgement: JudgementV2 }) {
  const title = new Map(judgement.groups.map((g) => [g.key, g.title]));
  const rank = (c: JudgeCriterion) => {
    const i = GROUP_ORDER.indexOf(c.group);
    return i < 0 ? GROUP_ORDER.length : i;
  };
  const criteria = [...judgement.criteria].sort((a, b) => rank(a) - rank(b));
  return (
    <Card className="min-w-0 overflow-hidden">
      <CardHead
        title="기준별 판정"
        hint={`기준 ${criteria.length} · 감점 ${judgement.penalties.length}${judgement.dropped.length ? ` · 중복 제외 ${judgement.dropped.length}` : ""}`}
      />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-[13px]">
          <caption className="sr-only">기준별 판정 · 가중치 · 기여 · 근거</caption>
          <thead>
            <tr className="border-b border-border text-left text-[11px] text-muted-foreground">
              <th className="px-4 py-2 font-normal">기준</th>
              <th className="px-2 py-2 font-normal">그룹</th>
              <th className="px-2 py-2 font-normal">판정</th>
              <th className="px-2 py-2 text-right font-normal" title="그룹 안에서의 비중 (score_impact)">
                가중치
              </th>
              <th className="px-2 py-2 text-right font-normal" title="Final 에서 이 기준이 차지하는 몫">
                실효
              </th>
              <th
                className="px-2 py-2 text-right font-normal"
                title="실효 가중치 × 판정값 (충족 1 · 일부 0.5 · 미충족 0)"
              >
                기여
              </th>
              <th className="px-4 py-2 font-normal">근거</th>
            </tr>
          </thead>
          <tbody>
            {criteria.map((c) => (
              <tr key={c.id} className="border-b border-border align-top last:border-b-0">
                <td className="px-4 py-2">
                  <span className="font-mono text-[12px]">{c.id}</span>
                  {c.name && <span className="block text-[11px] text-muted-foreground">{c.name}</span>}
                </td>
                <td className="px-2 py-2 text-muted-foreground">
                  {title.get(c.group) ?? c.group}
                  {c.evaluator && c.evaluator !== "llm" && <span className="block text-[11px]">{c.evaluator}</span>}
                </td>
                <td className="px-2 py-2">
                  <VerdictChip verdict={c.verdict} raw={c.rawVerdict} />
                </td>
                <td className="px-2 py-2 text-right tabular-nums">{w3(c.weight)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{w3(c.effectiveWeight)}</td>
                <td className="px-2 py-2 text-right tabular-nums">
                  {c.verdict === "not_applicable" ? (
                    <span className="text-muted-foreground">제외</span>
                  ) : (
                    w3(c.contribution)
                  )}
                </td>
                <td className="px-4 py-2">
                  {c.note || <span className="text-muted-foreground">—</span>}
                  {c.measured && (
                    <span className="block text-[11px] text-muted-foreground">{measuredText(c.measured)}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border-t border-border px-4 py-3">
        <p className="mb-2 text-xs font-medium">
          감점{" "}
          <span className="font-normal text-muted-foreground">
            · 발생했을 때만 깎는다 · 같은 지연을 두 번 깎지 않도록 이미 깎인 만큼 뺀다 (R6.1 상계)
          </span>
        </p>
        {judgement.penalties.length === 0 ? (
          <p className="text-[13px] text-muted-foreground">발동한 감점이 없습니다.</p>
        ) : (
          <ul className="grid gap-2">
            {judgement.penalties.map((p) => (
              <PenaltyRow key={p.id} penalty={p} />
            ))}
          </ul>
        )}
        {judgement.dropped.length > 0 && (
          <ul className="mt-2 grid gap-0.5 text-[12px] text-muted-foreground">
            {judgement.dropped.map((d) => (
              <li key={d.id}>
                <span className="font-mono">{d.id}</span> — 같은 오류의 감점이 겹쳐 빠졌습니다
                {d.reason ? ` (${d.reason.replace(/^overlap_group:/, "overlap_group ")})` : ""}
                {d.kept && (
                  <>
                    {" "}
                    → <span className="font-mono">{d.kept}</span> 가 적용됨
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

function PenaltyRow({ penalty: p }: { penalty: JudgePenalty }) {
  const offset = p.compensatedBy != null && p.compensatedBy > 0;
  return (
    <li className="grid gap-2 text-[13px] sm:grid-cols-[minmax(0,220px)_auto_minmax(0,1fr)]">
      <span>
        <span className="font-mono text-[12px]">{p.id}</span>
        {p.overlapGroup && <span className="block text-[11px] text-muted-foreground">overlap {p.overlapGroup}</span>}
      </span>
      <VerdictChip verdict={p.verdict} raw={p.rawVerdict} penalty />
      <span className="grid gap-0.5">
        <span className="tabular-nums">
          {offset ? (
            <>
              명목 {signed3(p.nominal)} → 적용 <strong>{signed3(p.applied)}</strong>
              <span className="text-muted-foreground">
                {" "}
                · 상계 {w3(p.compensatedBy)} (보상하는 기준이 이미 깎인 만큼)
              </span>
            </>
          ) : (
            <>적용 {signed3(p.applied ?? p.nominal)}</>
          )}
        </span>
        {(p.errorRef || p.note) && (
          <span className="text-muted-foreground">{[p.errorRef, p.note].filter(Boolean).join(" · ")}</span>
        )}
      </span>
    </li>
  );
}
